// SPA meta-tag manager. Each public page mounts and calls setPageMeta
// with its own title / description / canonical / og image / JSON-LD;
// the helper updates (or creates) the relevant <head> elements.
//
// Tags are not reset on unmount — the next route that cares about
// SEO sets its own values. For the bot crawl path (initial HTML
// before React hydrates), functions/[[path]].ts injects route-aware
// meta server-side via Cloudflare Pages Functions + HTMLRewriter.
// The JSON-LD <script> tag is removed when no jsonLd is provided so
// a non-Article page (like the index) doesn't carry stale Article
// markup from the last guide visited.

export interface PageMeta {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown>;
}

const DEFAULT_OG_IMAGE = "https://casethread.app/og-image.png";

function setMeta(
  attr: "name" | "property",
  key: string,
  value: string,
): void {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = value;
}

function setCanonical(url: string): void {
  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = url;
}

const JSONLD_ID = "page-jsonld";

function setJsonLd(data: Record<string, unknown> | undefined): void {
  const existing = document.getElementById(JSONLD_ID);
  if (!data) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.textContent = JSON.stringify(data);
    return;
  }
  const script = document.createElement("script");
  script.id = JSONLD_ID;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export function setPageMeta(meta: PageMeta): void {
  document.title = meta.title;
  setMeta("name", "description", meta.description);
  const og = meta.ogImage ?? DEFAULT_OG_IMAGE;
  setMeta("property", "og:title", meta.title);
  setMeta("property", "og:description", meta.description);
  setMeta("property", "og:url", meta.canonical);
  setMeta("property", "og:image", og);
  setMeta("name", "twitter:title", meta.title);
  setMeta("name", "twitter:description", meta.description);
  setMeta("name", "twitter:image", og);
  setCanonical(meta.canonical);
  setJsonLd(meta.jsonLd);
}
